import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import TableContainer from "../../components/Common/TableContainer";
import CardPagination from "../../components/Common/CardPagination";
import swal from "sweetalert";
import { delReservation, getAllreservation, putReservation } from "helpers/requests";
import { createPayement } from "helpers/requests"; // Import the payment creation function
import PaymentFormModal from "../../components/Common/PaymentFormModal";

const Voyage = (props) => {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Voyage";

    const fetchReservations = async () => {
      try {
        const response = await getAllreservation();
        setReservations(response);
        setFilteredReservations(response); // Initialize filtered data
      } catch (error) {
        console.error("Error fetching reservations:", error);
        swal("Error!", "Could not fetch reservation data.", "error");
      }
    };

    fetchReservations();
  }, []);

  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("vf_search") || "");
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("vf_status") || "all");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("vf_sort") || "default");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [monthFilter, setMonthFilter] = useState(() => localStorage.getItem("vf_month") || "all");
  const [yearFilter, setYearFilter] = useState(() => localStorage.getItem("vf_year") || "all");

  // Payment modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => parseInt(localStorage.getItem("vf_pageSize") || "12", 10));

  // Persist filters
  useEffect(() => {
    localStorage.setItem("vf_search", searchTerm);
    localStorage.setItem("vf_status", statusFilter);
    localStorage.setItem("vf_sort", sortBy);
    localStorage.setItem("vf_month", monthFilter);
    localStorage.setItem("vf_year", yearFilter);
    localStorage.setItem("vf_pageSize", String(pageSize));
  }, [searchTerm, statusFilter, sortBy, monthFilter, yearFilter, pageSize]);

  // Reset to first page whenever filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, sortBy, monthFilter, yearFilter, pageSize]);

  // Generate years for dropdown (current year and previous 5 years)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return ["all", ...Array.from({ length: 5 }, (_, i) => currentYear - i)];
  }, []);

  // Generate months for dropdown
  const months = useMemo(() => [
    "all", "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ], []);

  // Handle search, filters, and sorting logic
  useEffect(() => {
    let updatedReservations = [...reservations];

    // Filter by status
    if (statusFilter !== "all") {
      updatedReservations = updatedReservations.filter(
        (res) => res.status === statusFilter
      );
    }

    // Filter by month
    if (monthFilter !== "all") {
      const monthIndex = months.indexOf(monthFilter) - 1;
      updatedReservations = updatedReservations.filter((res) => {
        const resDate = new Date(res.checkin_date);
        return resDate.getMonth() === monthIndex;
      });
    }

    // Filter by year
    if (yearFilter !== "all") {
      updatedReservations = updatedReservations.filter((res) => {
        const resDate = new Date(res.checkin_date);
        return resDate.getFullYear() === parseInt(yearFilter);
      });
    }

    // Filter by search term
    if (searchTerm) {
      updatedReservations = updatedReservations.filter((res) =>
        `${res.nom} ${res.surnom} ${res.email}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    // Sort by creation date
    if (sortBy === "newest") {
      updatedReservations.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    }

    setFilteredReservations(updatedReservations);
  }, [searchTerm, statusFilter, monthFilter, yearFilter, sortBy, reservations]);

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredReservations.map((res) => res.id));
    }
    setIsSelectAllChecked(!isSelectAllChecked);
  };

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleCheck = (id) => {
    navigate(`/reservation/${id}`);
  };

  const handlePaid = (reservation) => {
    setPaymentTarget(reservation);
    setPaymentModalOpen(true);
  };

  const submitPayment = async (payload) => {
    if (!paymentTarget) return;
    setPaymentSubmitting(true);
    try {
      await createPayement(payload);
      await putReservation(paymentTarget.id, { ...paymentTarget, ispayed: true });

      const data = await getAllreservation();
      setReservations(data);
      setFilteredReservations(data);

      setPaymentModalOpen(false);
      setPaymentTarget(null);
      swal("Réservation payée!", { icon: "success" });
    } catch (error) {
      console.error("Error processing payment:", error);
      swal("Erreur", "Impossible de traiter le paiement", "error");
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleDelete = (reservation) => {
    swal({
      title: "Êtes-vous sûr?",
      text: "Une fois supprimé, vous ne pourrez pas récupérer cette réservation",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        await delReservation(reservation.id);
        setReservations(reservations.filter((res) => res.id !== reservation.id));
        swal("Réservation supprimée!", { icon: "success" });
      } else {
        swal("Suppression annulée");
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                id="checkAll"
                checked={isSelectAllChecked}
                onChange={handleSelectAll}
              />
            </Label>
          </FormGroup>
        ),
        accessorKey: "id",
        cell: (cellProps) => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                checked={selectedRows.includes(cellProps.row.original.id)}
                onChange={() => handleRowSelect(cellProps.row.original.id)}
              />
            </Label>
          </FormGroup>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Nom",
        accessorKey: "nom",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Surnom",
        accessorKey: "surnom",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Date de demande",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) =>
          cellProps.row.original.createdAt
            ? new Date(cellProps.row.original.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
            : "-",
      },
      {
        header: "Check-in",
        accessorKey: "checkin_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) =>
          new Date(cellProps.row.original.checkin_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }),
      },
      {
        header: "Check-out",
        accessorKey: "checkout_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) =>
          new Date(cellProps.row.original.checkout_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }),
      },
      {
        header: "Total Amount",
        accessorKey: "totalAmount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) =>
          `${parseFloat(cellProps.row.original.totalAmount).toFixed(2)} DA`,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => (
          <Badge
            color={
              cellProps.row.original.status === "PENDING"
                ? "warning"
                : cellProps.row.original.status === "ACCEPTED"
                ? "success"
                : "danger"
            }
            className="font-size-10"
          >
            {cellProps.row.original.status}
          </Badge>
        ),
      },
      {
        header: "Payement",
        accessorKey: "ispayed",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => (
          <Badge
            color={
              cellProps.row.original.ispayed === true
                ? "success"  // Green for paid
                : "warning"  // Yellow/warning for unpaid
            }
            className="font-size-10"
          >
            {cellProps.row.original.ispayed ? "Paid" : "Not Paid"}
          </Badge>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <span
              className="text-primary"
              style={{ cursor: "pointer" }}
              id={`checktooltip-${cellProps.row.original.id}`}
              onClick={() => handleCheck(cellProps.row.original.id)}
            >
              <i className="mdi mdi-eye font-size-18" />
              <UncontrolledTooltip
                placement="top"
                target={`checktooltip-${cellProps.row.original.id}`}
              >
                Detail
              </UncontrolledTooltip>
            </span>
            <span
              className="text-danger"
              style={{ cursor: "pointer" }}
              onClick={() => handleDelete(cellProps.row.original)}
            >
              <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${cellProps.row.original.id}`} />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id}`}>
                Delete
              </UncontrolledTooltip>
            </span>
            {!cellProps.row.original.ispayed && (
              <span
                className="text-success"
                style={{ cursor: "pointer" }}
                id={`paidtooltip-${cellProps.row.original.id}`}
                onClick={() => handlePaid(cellProps.row.original)}
              >
                <i className="mdi mdi-currency-usd font-size-18" />
                <UncontrolledTooltip
                  placement="top"
                  target={`paidtooltip-${cellProps.row.original.id}`}
                >
                  Check payment
                </UncontrolledTooltip>
              </span>
            )}
          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked, filteredReservations]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Voyage")}
            breadcrumbItem={props.t("Voyage")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle style={{ marginBottom: "40px" }}>
                    {props.t("Réservation de Voyage")}
                  </CardTitle>

                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md="3">
                      <FormGroup>
                        <Label for="statusFilter">Filter by Status</Label>
                        <Input
                          type="select"
                          id="statusFilter"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">all</option>
                          <option value="PENDING">Pending</option>
                          <option value="ACCEPTED">Accepted</option>
                          <option value="REFUSED">Refused</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="monthFilter">Filter by Month</Label>
                        <Input
                          type="select"
                          id="monthFilter"
                          value={monthFilter}
                          onChange={(e) => setMonthFilter(e.target.value)}
                        >
                          {months.map((month) => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="yearFilter">Filter by Year</Label>
                        <Input
                          type="select"
                          id="yearFilter"
                          value={yearFilter}
                          onChange={(e) => setYearFilter(e.target.value)}
                        >
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="sortBy">Sort by</Label>
                        <Input
                          type="select"
                          id="sortBy"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          <option value="default">Default</option>
                          <option value="newest">Newest</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Search Input */}
                  <Row className="mb-3">
                    <Col md="12">
                      <FormGroup>
                        <Label for="searchInput">Search</Label>
                        <Input
                          type="text"
                          id="searchInput"
                          placeholder="Search by nom, surnom, or email"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {filteredReservations.length === 0 ? (
                    <div className="text-center text-muted py-5 border border-light rounded-3">
                      <i className="mdi mdi-airplane-off display-4 d-block mb-2" />
                      <h5 className="mt-3">Aucune réservation</h5>
                      <p className="mb-0">Aucun résultat ne correspond aux filtres.</p>
                    </div>
                  ) : (
                    <Row>
                      {filteredReservations
                        .slice((page - 1) * pageSize, page * pageSize)
                        .map((res) => {
                          const statusColor =
                            res.status === "PENDING" ? "warning"
                            : res.status === "ACCEPTED" ? "success"
                            : "danger";
                          const cite = (res.voyage?.cite || res.voyageOrganize?.cite || "—").slice(0, 4).toUpperCase();
                          const homeCode = (res.nationality || "DZA").slice(0, 3).toUpperCase();
                          return (
                            <Col xl={4} lg={6} md={6} key={res.id}>
                              {/* ── BOARDING-PASS CARD — soft peach accent + city codes ── */}
                              <Card
                                className="border h-100 overflow-hidden position-relative"
                                style={{
                                  borderColor: "#f5d9c2 !important",
                                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
                                }}
                              >
                                {/* perforation circles on the left edge to mimic a ticket */}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: -8,
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    background: "#f4f6fa",
                                    zIndex: 1,
                                  }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    right: -8,
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    background: "#f4f6fa",
                                    zIndex: 1,
                                  }}
                                />

                                {/* Top header — soft peach band with passenger + status */}
                                <div
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #fff4e6 0%, #ffe7d0 100%)",
                                    padding: "12px 18px",
                                    borderBottom: "1px solid #f5d9c2",
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div style={{ minWidth: 0 }} className="flex-grow-1">
                                      <small
                                        className="text-uppercase d-block fw-bold"
                                        style={{ color: "#b85c00", fontSize: 10, letterSpacing: 1 }}
                                      >
                                        <i className="bx bx-plane me-1" />
                                        Passager
                                      </small>
                                      <strong
                                        className="text-truncate d-block"
                                        style={{ color: "#7a3d00" }}
                                      >
                                        {res.nom} {res.surnom}
                                      </strong>
                                    </div>
                                    <Badge color={statusColor} className="font-size-10">
                                      {res.status}
                                    </Badge>
                                  </div>
                                </div>

                                {/* Itinerary — FROM → ✈ → TO with big city codes */}
                                <div
                                  className="px-3 py-3"
                                  style={{ borderBottom: "1px dashed #f5d9c2" }}
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="text-center">
                                      <h3 className="mb-0 fw-bold" style={{ color: "#7a3d00" }}>
                                        {homeCode}
                                      </h3>
                                      <small className="text-muted">
                                        {new Date(res.checkin_date).toLocaleDateString("fr-FR", {
                                          day: "numeric",
                                          month: "short",
                                        })}
                                      </small>
                                    </div>
                                    <div className="text-center px-2 flex-grow-1 position-relative">
                                      <i
                                        className="bx bx-plane-take-off"
                                        style={{ fontSize: 28, color: "#e89160" }}
                                      />
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "50%",
                                          left: 0,
                                          right: 0,
                                          height: 2,
                                          borderTop: "2px dashed #e89160",
                                          zIndex: -1,
                                        }}
                                      />
                                    </div>
                                    <div className="text-center">
                                      <h3 className="mb-0 fw-bold" style={{ color: "#7a3d00" }}>
                                        {cite}
                                      </h3>
                                      <small className="text-muted">
                                        {new Date(res.checkout_date).toLocaleDateString("fr-FR", {
                                          day: "numeric",
                                          month: "short",
                                        })}
                                      </small>
                                    </div>
                                  </div>
                                </div>

                                {/* Stub — passengers + amount */}
                                <CardBody className="pt-3 pb-3">
                                  <Row className="g-2 mb-2">
                                    <Col xs={4}>
                                      <small className="text-muted text-uppercase d-block font-size-10">
                                        <i className="bx bx-user me-1" />Voyageurs
                                      </small>
                                      <strong className="font-size-12">
                                        {(res.n_adult || 0) + (res.n_enfant || 0) + (res.n_bebe || 0)}
                                      </strong>
                                    </Col>
                                    <Col xs={4}>
                                      <small className="text-muted text-uppercase d-block font-size-10">
                                        <i className="bx bx-package me-1" />Voyage
                                      </small>
                                      <strong className="font-size-12 text-truncate d-block">
                                        {res.voyage?.code || res.voyageOrganize?.code || "—"}
                                      </strong>
                                    </Col>
                                    <Col xs={4} className="text-end">
                                      <small className="text-muted text-uppercase d-block font-size-10">
                                        Total
                                      </small>
                                      <strong
                                        className="font-size-13"
                                        style={{ color: "#b85c00" }}
                                      >
                                        {parseFloat(res.totalAmount || 0).toLocaleString("fr-DZ")} DA
                                      </strong>
                                    </Col>
                                  </Row>

                                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                    <Badge
                                      color={res.ispayed ? "success" : "warning"}
                                      className="font-size-10"
                                    >
                                      <i className={`mdi ${res.ispayed ? "mdi-check-circle" : "mdi-clock-outline"} me-1`} />
                                      {res.ispayed ? "Payé" : "Non payé"}
                                    </Badge>
                                    <div className="d-flex gap-3">
                                      <span
                                        className="text-primary"
                                        style={{ cursor: "pointer" }}
                                        id={`cardview-${res.id}`}
                                        onClick={() => handleCheck(res.id)}
                                      >
                                        <i className="mdi mdi-eye font-size-18" />
                                        <UncontrolledTooltip placement="top" target={`cardview-${res.id}`}>
                                          Détails
                                        </UncontrolledTooltip>
                                      </span>
                                      <span
                                        className="text-danger"
                                        style={{ cursor: "pointer" }}
                                        id={`carddel-${res.id}`}
                                        onClick={() => handleDelete(res)}
                                      >
                                        <i className="mdi mdi-delete font-size-18" />
                                        <UncontrolledTooltip placement="top" target={`carddel-${res.id}`}>
                                          Supprimer
                                        </UncontrolledTooltip>
                                      </span>
                                      {!res.ispayed && (
                                        <span
                                          className="text-success"
                                          style={{ cursor: "pointer" }}
                                          id={`cardpay-${res.id}`}
                                          onClick={() => handlePaid(res)}
                                        >
                                          <i className="mdi mdi-currency-usd font-size-18" />
                                          <UncontrolledTooltip placement="top" target={`cardpay-${res.id}`}>
                                            Enregistrer le paiement
                                          </UncontrolledTooltip>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                    </Row>
                  )}
                  {filteredReservations.length > 0 && (
                    <CardPagination
                      page={page}
                      pageSize={pageSize}
                      total={filteredReservations.length}
                      onPageChange={setPage}
                      onPageSizeChange={setPageSize}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <PaymentFormModal
          isOpen={paymentModalOpen}
          toggle={() => setPaymentModalOpen(false)}
          type="RESERVATION"
          linkId={paymentTarget?.id}
          sourceLabel={
            paymentTarget
              ? `Voyage ${paymentTarget.nom || ""} ${paymentTarget.surnom || ""}`.trim()
              : ""
          }
          prefill={{
            firstname: paymentTarget?.nom || "",
            lastname: paymentTarget?.surnom || "",
            phonenumber: paymentTarget?.user?.phone || "",
            email: paymentTarget?.email || "",
            amount: paymentTarget?.totalAmount || "",
          }}
          submitting={paymentSubmitting}
          onSubmit={submitPayment}
        />
      </div>
    </React.Fragment>
  );
};

Voyage.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(Voyage);